import { useCallback } from 'react';
import { Resetter, atom, useRecoilState, useResetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { generateClientToken } from 'shared/utils';
import {
  MembershipPlan,
  MEMBERSHIP_PLANS,
} from 'features/membership/membership.constants';
import { localStorageEffect } from 'libs/recoil';
import { getData, LOCAL_STORAGE_KEY } from 'state/localStorage';

/** Recovery replay path: `'upgrade'` -> `upgrade_membership`, `'new'` -> `iap_create`. */
export type IapPurchaseType = 'new' | 'upgrade';

export type IapCheckoutDetails = {
  clientToken: string;
  externalAccountCode: string;
  email: string;
  firstName: string;
  lastName: string;
  optInToUpdates: boolean;
  verificationToken: string;
  /**
   * Epoch ms the order was placed; `0` when idle. Marks an interrupted purchase
   * for recovery, and is the cutoff separating a fresh charge from a collision.
   */
  checkoutStartedAt: number;
  /** Persisted at order time so recovery need not infer it from async auth. */
  purchaseType: IapPurchaseType;
  /**
   * Buyer's user id for logged-in checkouts, else `null`. Binds an upgrade
   * replay to the original account.
   */
  accountUserId: number | null;
};

export type IapProductId =
  (typeof IAP_PRODUCT_IDS)[keyof typeof IAP_PRODUCT_IDS];

export type IapState = {
  checkoutDetails: IapCheckoutDetails;
  selectedProductId: IapProductId;
  plan: MembershipPlan;
  loading: boolean;
};

type UseIapState = IapState & {
  setCheckoutDetails: (details: Partial<IapCheckoutDetails>) => void;
  setSelectedProductId: (plan: MembershipPlan) => void;
  setLoading: (loading: boolean) => void;
  resetIapState: Resetter;
};

export const IAP_PRODUCT_IDS = {
  [MEMBERSHIP_PLANS.yearly]: 'wd_annual_recurring_membership',
  [MEMBERSHIP_PLANS.proYearly]: 'wd_pro_yearly_iap',
  [MEMBERSHIP_PLANS.proMonthly]: 'wd_pro_monthly_iap',
} as const;

export const MEMBERSHIP_TITLE_TRANS_KEYS = {
  [MEMBERSHIP_PLANS.yearly]:
    'membershipCheckout.sections.membership.title.regular',
  [MEMBERSHIP_PLANS.proYearly]:
    'membershipCheckout.sections.membership.title.pro',
  [MEMBERSHIP_PLANS.proMonthly]:
    'membershipCheckout.sections.membership.title.proMonthly',
};

export const MEMBERSHIP_DESCRIPTION_TRANS_KEYS = {
  [MEMBERSHIP_PLANS.yearly]:
    'membershipCheckout.sections.membership.description.yearly',
  [MEMBERSHIP_PLANS.proYearly]:
    'membershipCheckout.sections.membership.description.yearly',
  [MEMBERSHIP_PLANS.proMonthly]:
    'membershipCheckout.sections.membership.description.monthly',
};

const DEFAULT_IAP_STATE: IapState = {
  checkoutDetails: {
    clientToken: generateClientToken(),
    externalAccountCode: uuidv4(),
    email: '',
    firstName: '',
    lastName: '',
    optInToUpdates: false,
    verificationToken: '',
    checkoutStartedAt: 0,
    purchaseType: 'new',
    accountUserId: null,
  },
  selectedProductId: IAP_PRODUCT_IDS.yearly,
  plan: MEMBERSHIP_PLANS.yearly,
  loading: false,
};

const iapStateAtom = atom({
  key: LOCAL_STORAGE_KEY.IAP_STATE,
  default: DEFAULT_IAP_STATE,
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.IAP_STATE)],
});

export const getPersistedIapState = (): IapState | null =>
  getData<IapState>(LOCAL_STORAGE_KEY.IAP_STATE) ?? null;

export const useIapState = (): UseIapState => {
  const resetIapState = useResetRecoilState(iapStateAtom);
  const [iapState, setIapState] = useRecoilState(iapStateAtom);

  const setCheckoutDetails = useCallback(
    (details: Partial<IapCheckoutDetails>) => {
      setIapState((prev) => ({
        ...prev,
        checkoutDetails: { ...prev.checkoutDetails, ...details },
      }));
    },
    [setIapState],
  );

  const setSelectedProductId = useCallback(
    (plan: MembershipPlan) => {
      setIapState((prev) => ({
        ...prev,
        selectedProductId: IAP_PRODUCT_IDS[plan],
        plan,
      }));
    },
    [setIapState],
  );

  const setLoading = useCallback(
    (loading: boolean) => {
      setIapState((prev) => ({ ...prev, loading }));
    },
    [setIapState],
  );

  return {
    loading: iapState.loading,
    checkoutDetails: iapState.checkoutDetails,
    selectedProductId: iapState.selectedProductId,
    plan: iapState.plan,
    setCheckoutDetails,
    setSelectedProductId,
    setLoading,
    resetIapState,
  };
};
